import { createContext, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import {
  getProfileRequest,
  loginRequest,
  logoutRequest,
} from "@services/auth/authApi";

const AuthContext = createContext(null);
const CMS_AUTH_BODY_COOKIE = "cms_auth_body";
const LEGACY_AUTH_BODY_COOKIE = "cms_ui_state";
const CMS_UI_STATE_SECRET = process.env.REACT_APP_CMS_UI_STATE_SECRET;
const CUSTOMER_ROLE = "customer";

function normalizeRole(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function resolvePrimaryRole(authBody) {
  const roleFromAuthorization = normalizeRole(
    authBody?.authorization?.primaryRole,
  );
  if (roleFromAuthorization) return roleFromAuthorization;
  const roleFromUser = normalizeRole(authBody?.user?.role);
  if (roleFromUser) return roleFromUser;
  return "";
}

function isCustomerRole(authBody) {
  return resolvePrimaryRole(authBody) === CUSTOMER_ROLE;
}
function mergeAuthBodies(base, incoming) {
  const safeBase = base && typeof base === "object" ? base : {};
  const safeIncoming = incoming && typeof incoming === "object" ? incoming : {};

  return {
    ...safeBase,
    ...safeIncoming,
    user: {
      ...(safeBase.user || {}),
      ...(safeIncoming.user || {}),
    },
    authorization: {
      ...(safeBase.authorization || {}),
      ...(safeIncoming.authorization || {}),
    },
  };
}

function readAuthBodyCookie() {
  try {
    const raw = Cookies.get(CMS_AUTH_BODY_COOKIE);
    if (!raw) return null;
    if (!CMS_UI_STATE_SECRET) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
          "CMS auth cookie secret is missing (REACT_APP_CMS_UI_STATE_SECRET or REACT_APP_CMS_AUTH_BODY_SECRET).",
        );
      }
      return null;
    }
    const bytes = CryptoJS.AES.decrypt(raw, CMS_UI_STATE_SECRET);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    if (!decrypted) return null;
    const parsed = JSON.parse(decrypted);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
}

function writeAuthBodyCookie(body) {
  if (!CMS_UI_STATE_SECRET) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "CMS auth cookie not persisted because secret is missing (REACT_APP_CMS_UI_STATE_SECRET or REACT_APP_CMS_AUTH_BODY_SECRET).",
      );
    }
    return;
  }
  const encrypted = CryptoJS.AES.encrypt(
    JSON.stringify(body || null),
    CMS_UI_STATE_SECRET,
  ).toString();
  Cookies.set(CMS_AUTH_BODY_COOKIE, encrypted, {
    expires: 7,
    sameSite: "Lax",
  });
}

function clearAuthBodyCookie() {
  Cookies.remove(CMS_AUTH_BODY_COOKIE);
  Cookies.remove(LEGACY_AUTH_BODY_COOKIE);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authorization, setAuthorization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  function applyAuth(nextAuthBody) {
    const merged = mergeAuthBodies(
      { user, authorization },
      nextAuthBody || null,
    );
    setUser(merged.user || null);
    setAuthorization(merged.authorization || null);
  }

  function clearAuth() {
    setUser(null);
    setAuthorization(null);
  }

  useEffect(() => {
    let isMounted = true;

    async function bootstrapSession() {
      setIsLoading(true);
      const cachedBody = readAuthBodyCookie();
      if (cachedBody && isMounted) {
        applyAuth(cachedBody);
      }

      try {
        const response = await getProfileRequest();
        if (!isMounted) return;
        const body = mergeAuthBodies(cachedBody, response?.body || null);
        if (isCustomerRole(body)) {
          throw new Error(
            "Los usuarios con rol customer no pueden ingresar al CMS.",
          );
        }
        applyAuth(body);
        writeAuthBodyCookie(body);
      } catch (error) {
        if (!isMounted) return;
        clearAuth();
        clearAuthBodyCookie();
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  async function login(credentials) {
    return loginRequest(credentials);
  }

  async function logout() {
    try {
      await logoutRequest();
    } finally {
      clearAuth();
      clearAuthBodyCookie();
    }
  }

  function setAuthenticatedUser(nextAuth) {
    if (nextAuth && typeof nextAuth === "object" && "user" in nextAuth) {
      if (isCustomerRole(nextAuth)) {
        clearAuth();
        clearAuthBodyCookie();
        throw new Error("Tu rol customer no tiene acceso al CMS.");
      }
      applyAuth(nextAuth);
      writeAuthBodyCookie(nextAuth);
      return;
    }

    const body = { user: nextAuth || null, authorization: null };
    if (isCustomerRole(body)) {
      clearAuth();
      clearAuthBodyCookie();
      throw new Error("Tu rol customer no tiene acceso al CMS.");
    }
    applyAuth(body);
    writeAuthBodyCookie(body);
  }

  const value = useMemo(
    () => ({
      user,
      authorization,
      isLoading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      setAuthenticatedUser,
    }),
    [user, authorization, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return context;
}
